import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { ValidatedTextInput } from '../../../components/ValidatedTextInput';
import { useLoader } from '../../../helpers/useLoader';
import { InputStatus } from '../../../models/error.model';
import { IIntegrationsService } from '../integrations.service';

interface IProps {
  apiKeyId: string;
  onSubmit: () => void;
  onCancel: () => void;
  showModal: boolean;
}

export const EditApiKeyModalContainer = observer((props: IProps): ReactElement => {
  const service = useInjection(IIntegrationsService.$);

  const { apiKeyId, onSubmit, onCancel, showModal } = props;
  const { withLoaderAsync } = useLoader();
  const { t } = useTranslator();

  const [id, setId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [nameStatus, setNameStatus] = useState<InputStatus>(InputStatus.empty());
  const [isActive, setIsActive] = useState<boolean>(true);
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);

  const reloadApiKey = (): void => {
    const apiKey = service.store.apiKeys.find((x) => x.id === apiKeyId);

    if (!apiKey) {
      return;
    }

    setId(apiKey.id);
    setName(apiKey.name);
    setIsActive(apiKey.isActive);
    setRequestInProgress(false);
    setNameStatus(InputStatus.empty());
  };
  useEffect(() => {
    if (showModal) {
      reloadApiKey();
    }
  }, [showModal]);

  const handleNameChange = (name: string): void => {
    setName(name);
    validateName(name);
  };

  const validateName = (value: string): void => {
    let isValid = true;
    const errorCodes: TranslationKey[] = [];

    if (value.trim() === '') {
      isValid = false;
      errorCodes.push('field_cant_be_empty');
    }

    if (!service.isNameUnique(value, apiKeyId)) {
      isValid = false;
      errorCodes.push('api_key_name_must_be_unique');
    }

    setNameStatus(new InputStatus(isValid, errorCodes));
  };

  const handleSubmitAsync = async (): Promise<void> => {
    setRequestInProgress(true);
    await service.editApiKey({
      id,
      name: name.trim(),
      isActive,
    });
    setRequestInProgress(false);
    onSubmit();
  };

  const handleSubmitClickedAsync = async (): Promise<void> => {
    await withLoaderAsync(async () => handleSubmitAsync(), 'api-key-add-button');
  };

  const toggleIsActiveStatus = (): void => {
    setIsActive((prev) => !prev);
  };

  return (
    <S_Modal
      isOpen={showModal}
      toggle={onCancel}
      headerText={t('edit_api_key')}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
          <ButtonWithLoader
            id='api-key-add-button'
            loaderKey='api-key-add-button'
            onClick={handleSubmitClickedAsync}
            disabled={(!nameStatus.isValid && !nameStatus.isEmpty) || requestInProgress}
          >
            {t('save')}
          </ButtonWithLoader>
        </>
      }
    >
      <ValidatedTextInput
        id='api-key-name-input'
        labelText={t('api_key')}
        placeholder={t('api_key_name')}
        value={name}
        onChange={handleNameChange}
        feedbacks={nameStatus.errorCodes}
        isValid={nameStatus.isValid}
      />
      <S_Checkbox
        onChange={toggleIsActiveStatus}
        checked={isActive}
        label={t('active')}
        id='api-key-is-active-input'
      />
    </S_Modal>
  );
});
